import { useState } from 'react';
import { Link, useRouteContext } from '@tanstack/react-router';
import { Lock, FileText, Envelope, CaretRight, DownloadSimple, Question, User, SpeakerHigh, GridFour, Info } from '@phosphor-icons/react';
import { Switch } from '@ark-ui/react/switch';
import { css, cx } from 'styled-system/css';
import { t } from '@/ui/i18n';
import type { ThemePreference, ThemeStore } from '@/application/session/ThemeStore';
import type { SoundStore } from '@/application/session/SoundStore';
import type { SkipFilledStore } from '@/application/session/SkipFilledStore';
import { useAuth } from '@/ui/components/auth';
import { useInstallPrompt } from '@/ui/lib/useInstallPrompt';
import { Skeleton } from '@/design-system';
import { AppShell } from './AppShell';
import { BackHeader } from './BackHeader';
import { SignInSheet } from './SignInSheet';
import { SegmentedControl } from './SegmentedControl';
import { SettingsRow } from './SettingsRow';

const title = css({
  fontFamily: 'wsDisplay',
  fontWeight: 'semibold',
  fontSize: '26px',
  lineHeight: '1.1',
  color: 'ws.jadeInk',
  // title sits inside the 16px-gap stack; the gap alone provides spacing
  margin: 0,
});

const stack = css({ display: 'flex', flexDirection: 'column', gap: '16px' });

const profile = css({
  display: 'flex',
  alignItems: 'center',
  gap: '13px',
  bg: 'ws.card',
  borderRadius: '18px',
  padding: '14px',
  boxShadow: '0 1px 2px rgba(33,75,64,0.05), 0 10px 22px rgba(33,75,64,0.08)',
});
const avatar = css({
  flex: 'none',
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  bg: 'ws.sakuraDark',
  color: 'white',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'wsDisplay',
  fontWeight: 'semibold',
  fontSize: '20px',
});
const profileLink = css({ textDecoration: 'none', cursor: 'pointer', transition: 'background-color 120ms', _hover: { bg: 'ws.sable' }, _focusVisible: { outline: '3px solid token(colors.ws.sakuraRose)', outlineOffset: '2px' } });
const profileName = css({ fontFamily: 'wsDisplay', fontWeight: 'semibold', fontSize: '17px', color: 'ws.jadeInk' });
const profileMeta = css({ fontFamily: 'wsUi', fontSize: '12px', fontWeight: 'bold', color: 'ws.khaki', opacity: 0.85, marginTop: '2px' });

const groupLabel = css({
  fontFamily: 'wsUi',
  fontSize: '11px',
  fontWeight: 'black',
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'ws.eyebrow',
  margin: '0 6px 7px',
});
const listCard = css({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  bg: 'ws.card',
  borderRadius: '18px',
  overflow: 'hidden',
  boxShadow: '0 1px 2px rgba(33,75,64,0.05)',
});

const chevron = css({ marginLeft: 'auto', flex: 'none', color: 'ws.khaki', opacity: 0.5, display: 'flex' });

const themeCard = css({ bg: 'ws.card', borderRadius: '18px', padding: '8px', boxShadow: '0 1px 2px rgba(33,75,64,0.05)' });

const THEME_OPTIONS: ReadonlyArray<{ id: ThemePreference; label: string }> = [
  { id: 'clair', label: t('v2.reglages.theme.clair') },
  { id: 'sombre', label: t('v2.reglages.theme.sombre') },
];

function ThemeGroup({ themeStore }: { readonly themeStore: ThemeStore }) {
  const [pref, setPref] = useState<ThemePreference>(() => themeStore.load());
  return (
    <section aria-label={t('v2.reglages.appearance')}>
      <div className={groupLabel}>{t('v2.reglages.appearance')}</div>
      <div className={themeCard}>
        <SegmentedControl
          mode="group"
          ariaLabel={t('v2.reglages.aria.theme')}
          options={THEME_OPTIONS}
          value={pref}
          onChange={(id) => {
            themeStore.set(id);
            setPref(id);
          }}
        />
      </div>
    </section>
  );
}

const soundCard = css({ bg: 'ws.card', borderRadius: '18px', padding: '4px', boxShadow: '0 1px 2px rgba(33,75,64,0.05)' });
const soundRow = css({ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', minHeight: '52px', padding: '12px 14px', cursor: 'pointer' });
const soundTile = css({ flex: 'none', width: '34px', height: '34px', borderRadius: '10px', bg: 'ws.jade', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'ws.jadeInk' });
const soundBody = css({ display: 'flex', flexDirection: 'column', minWidth: 0 });
const soundLabel = css({ fontSize: '14.5px', fontWeight: 'bold', color: 'ws.jadeInk' });
const soundSub = css({ fontFamily: 'wsUi', fontSize: '12px', fontWeight: 'bold', color: 'ws.khaki', opacity: 0.85, marginTop: '1px' });
const switchControl = css({
  marginLeft: 'auto',
  flex: 'none',
  width: '44px',
  height: '26px',
  borderRadius: '999px',
  padding: '3px',
  bg: 'ws.sable',
  transition: 'background 160ms ease',
  '&[data-state=checked]': { bg: 'ws.jade' },
  _focusVisible: { outline: '3px solid token(colors.ws.sakuraRose)', outlineOffset: '2px' },
});
const switchThumb = css({
  display: 'block',
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  bg: 'white',
  boxShadow: '0 1px 2px rgba(33,75,64,0.3)',
  transition: 'transform 160ms ease',
  '&[data-state=checked]': { transform: 'translateX(18px)' },
});

function SoundGroup({ soundStore }: { readonly soundStore: SoundStore }) {
  const [on, setOn] = useState<boolean>(() => soundStore.load());
  return (
    <section aria-label={t('v2.reglages.sound')}>
      <div className={groupLabel}>{t('v2.reglages.sound')}</div>
      <div className={soundCard}>
        <Switch.Root
          checked={on}
          onCheckedChange={(details) => {
            soundStore.set(details.checked);
            setOn(details.checked);
          }}
          className={soundRow}
        >
          <span className={soundTile}>
            <SpeakerHigh size={18} weight="bold" aria-hidden="true" />
          </span>
          <span className={soundBody}>
            <Switch.Label className={soundLabel}>{t('v2.reglages.sound.label')}</Switch.Label>
            <span className={soundSub}>{t('v2.reglages.sound.sub')}</span>
          </span>
          <Switch.Control className={switchControl}>
            <Switch.Thumb className={switchThumb} />
          </Switch.Control>
          {/* role=switch: an on/off toggle, not a tri-state checkbox (Ark's default input role). */}
          <Switch.HiddenInput role="switch" />
        </Switch.Root>
      </div>
    </section>
  );
}

function SkipFilledGroup({ skipFilledStore }: { readonly skipFilledStore: SkipFilledStore }) {
  const [on, setOn] = useState<boolean>(() => skipFilledStore.load());
  return (
    <section aria-label={t('v2.reglages.grid')}>
      <div className={groupLabel}>{t('v2.reglages.grid')}</div>
      <div className={soundCard}>
        <Switch.Root
          checked={on}
          onCheckedChange={(details) => {
            skipFilledStore.set(details.checked);
            setOn(details.checked);
          }}
          className={soundRow}
        >
          <span className={soundTile}>
            <GridFour size={18} weight="bold" aria-hidden="true" />
          </span>
          <span className={soundBody}>
            <Switch.Label className={soundLabel}>{t('v2.reglages.grid.skipFilled.label')}</Switch.Label>
            <span className={soundSub}>{t('v2.reglages.grid.skipFilled.sub')}</span>
          </span>
          <Switch.Control className={switchControl}>
            <Switch.Thumb className={switchThumb} />
          </Switch.Control>
          {/* role=switch: an on/off toggle, not a tri-state checkbox (Ark's default input role). */}
          <Switch.HiddenInput role="switch" />
        </Switch.Root>
      </div>
    </section>
  );
}

const foot = css({ fontFamily: 'wsMono', fontSize: '11px', color: 'ws.khaki', opacity: 0.85, textAlign: 'center', paddingTop: '10px' });

function initialFor(displayName: string): string {
  return ([...displayName][0] ?? '?').toLocaleUpperCase('fr-FR');
}

function ProfileCard() {
  const { state } = useAuth();
  const { authClient } = useRouteContext({ from: '__root__' });
  const [signInOpen, setSignInOpen] = useState(false);

  // Skeleton while whoami resolves so the subtext never flips guest→name on first paint.
  if (state.status === 'loading') {
    return (
      <div className={cx(profile)} role="status" aria-busy="true" aria-label={t('v2.reglages.account.loading')}>
        <span className={avatar} aria-hidden="true">
          <User size={24} weight="bold" />
        </span>
        <div>
          <Skeleton tone="onCard" width={120} height={16} radius={6} />
          <Skeleton tone="onCard" width={80} height={11} radius={6} style={{ marginTop: '5px' }} />
        </div>
      </div>
    );
  }

  if (state.status === 'authed') {
    return (
      <Link to="/compte" className={cx(profile, profileLink)}>
        <span className={avatar} aria-hidden="true">{initialFor(state.whoami.displayName)}</span>
        <div>
          <div className={profileName}>{state.whoami.displayName}</div>
          <div className={profileMeta}>{t('v2.reglages.profile.seeAccount')}</div>
        </div>
        <span className={chevron}>
          <CaretRight size={18} weight="bold" aria-hidden="true" />
        </span>
      </Link>
    );
  }

  // A guest signs in from here rather than at /compte, so the sheet's returnTo is this page.
  return (
    <>
      <button type="button" className={cx(profile, profileLink)} onClick={() => setSignInOpen(true)}>
        <span className={avatar} aria-hidden="true">
          <User size={24} weight="bold" />
        </span>
        <div>
          <div className={profileName}>{t('v2.reglages.profile.guest')}</div>
          <div className={profileMeta}>{t('v2.reglages.profile.noAccount')}</div>
        </div>
        <span className={chevron}>
          <CaretRight size={18} weight="bold" aria-hidden="true" />
        </span>
      </button>
      <SignInSheet
        open={signInOpen}
        authClient={authClient}
        onClose={() => setSignInOpen(false)}
        icon={User}
        title={t('v2.signin.account.title')}
        description={t('v2.signin.account.description')}
      />
    </>
  );
}

export function ReglagesScreen() {
  const { canInstall, promptInstall } = useInstallPrompt();
  const { themeStore, soundStore, skipFilledStore } = useRouteContext({ from: '__root__' });
  return (
    <AppShell variant="flow" topBar={<BackHeader to="/" />} backTo="/">
      <div className={stack}>
        <h1 className={title}>{t('v2.reglages.title')}</h1>

        <ProfileCard />

        {themeStore ? <ThemeGroup themeStore={themeStore} /> : null}

        {soundStore ? <SoundGroup soundStore={soundStore} /> : null}

        {skipFilledStore ? <SkipFilledGroup skipFilledStore={skipFilledStore} /> : null}

        {canInstall ? (
          <nav aria-label={t('v2.reglages.app')}>
            <div className={groupLabel}>{t('v2.reglages.app')}</div>
            <ul className={listCard}>
              <SettingsRow
                icon={DownloadSimple}
                label={t('v2.reglages.install.label')}
                sub={t('v2.reglages.install.sub')}
                onClick={promptInstall}
                last
              />
            </ul>
          </nav>
        ) : null}

        <nav aria-label={t('v2.reglages.about')}>
          <div className={groupLabel}>{t('v2.reglages.about')}</div>
          <ul className={listCard}>
            <SettingsRow icon={Lock} tone="soft" to="/confidentialite" label={t('v2.reglages.about.privacy')} />
            <SettingsRow icon={FileText} tone="soft" to="/mentions-legales" label={t('v2.reglages.about.legal')} />
            <SettingsRow
              icon={FileText}
              tone="soft"
              to="/conditions-abonnement"
              label={t('v2.reglages.about.terms')}
            />
            <SettingsRow icon={Info} tone="soft" to="/a-propos" label={t('v2.reglages.about')} last />
          </ul>
        </nav>

        <nav aria-label={t('v2.reglages.help')}>
          <div className={groupLabel}>{t('v2.reglages.help')}</div>
          <ul className={listCard}>
            <SettingsRow icon={Question} tone="soft" to="/aide" label={t('v2.reglages.help.shortcuts')} />
            <SettingsRow icon={Envelope} href="mailto:contact@wordsparrow.io" label={t('v2.reglages.help.contact')} last />
          </ul>
        </nav>

        <p className={foot}>{t('v2.reglages.foot')}</p>
      </div>
    </AppShell>
  );
}
